import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  LinearProgress,
  Chip,
  IconButton,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Fade,
  Zoom,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Badge,
  Divider,
  CircularProgress,
} from '@mui/material';
import NetworkMap from '../components/NetworkMap';
import SystemStatus from '../components/SystemStatus';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  NetworkCheck as NetworkIcon,
  Support as SupportIcon,
  AutoAwesome as MagicIcon,
  Notifications as NotificationsIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  HealthAndSafety as SystemStatusIcon,
  CloudDone as CloudIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
  TrendingDown as TrendingDownIcon,
  PhoneAndroid as PhoneIcon,
  Router as RouterIcon,
  AccountBalance as BillingIcon,
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
  Campaign as CampaignIcon,
  Psychology as PsychologyIcon,
  Assignment as AssignmentIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  Group as GroupIcon,
  Insights as InsightsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import ProCogiaLogo from '../components/ProCogiaLogo';

// Import ProCogia Theme
import { procogiaBrand } from '../theme/procogia';

// Animations
const animations = {
  float: keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
  `,
  pulse: keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  `,
  shimmer: keyframes`
    0% { background-position: -200px 0; }
    100% { background-position: calc(200px + 100%) 0; }
  `,
  slideIn: keyframes`
    0% { transform: translateY(30px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  `,
  countUp: keyframes`
    0% { transform: scale(0.8); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  `,
};

interface User {
  id: string;
  name: string;
  email: string;
  role: 'customer' | 'agent' | 'admin';
}

interface DashboardProps {
  user: User;
  onNotification: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

interface MetricCard {
  title: string;
  value: string | number;
  change: string;
  positive: boolean;
  icon: React.ReactNode;
  color: string;
  gradient: string;
}

interface Activity {
  id: string;
  type: 'customer' | 'network' | 'billing' | 'support';
  title: string;
  description: string;
  timestamp: string;
  icon: React.ReactNode;
  color: string;
}

interface CustomerSegment {
  id: string;
  name: string;
  count: number;
  revenue: number;
  churnRate: number;
  satisfactionScore: number;
  behaviorTrends: string[];
  color: string;
}

interface ChurnRiskCustomer {
  id: string;
  name: string;
  phone: string;
  email: string;
  churnProbability: number;
  riskFactors: string[];
  retentionOffers: string[];
  lastActivity: string;
  ltv: number;
}

interface PersonalizationOpportunity {
  customerId: string;
  customerName: string;
  opportunity: string;
  recommendation: string;
  confidence: number;
  potentialRevenue: number;
  triggerEvent: string;
  timeWindow: string;
}

interface ServiceOptimization {
  category: string;
  count: number;
  avgResolutionTime: number;
  satisfactionScore: number;
  trend: 'up' | 'down' | 'stable';
  topIssues: string[];
  recommendations: string[];
}

const ModernDashboard: React.FC<DashboardProps> = ({ user, onNotification }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [customerSegments, setCustomerSegments] = useState<CustomerSegment[]>([]);
  const [churnRiskCustomers, setChurnRiskCustomers] = useState<ChurnRiskCustomer[]>([]);
  const [personalizationOps, setPersonalizationOps] = useState<PersonalizationOpportunity[]>([]);
  const [serviceOptimizations, setServiceOptimizations] = useState<ServiceOptimization[]>([]);
  const [realTimeData, setRealTimeData] = useState({
    totalCustomers: 10000,
    activeSegments: 8,
    highRiskCustomers: 127,
    personalizationHits: 342,
    avgSatisfaction: 4.2,
    resolutionTime: 23.5,
  });

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 300000); // Refresh every 5 minutes instead of 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setCustomerSegments(getCustomerSegmentsForRole(user.role));
      setChurnRiskCustomers(getChurnRiskCustomersForRole(user.role));
      setPersonalizationOps(getPersonalizationOpsForRole(user.role));
      setServiceOptimizations(getServiceOptimizationsForRole(user.role));
      
      // Remove the notification that was causing chat resets
      // onNotification('Dashboard data refreshed', 'success');
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      onNotification('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerSegmentsForRole = (role: string): CustomerSegment[] => {
    const baseSegments = {
      admin: [
        {
          id: 'high-value',
          name: 'High Value Customers',
          count: 1200,
          revenue: 250000,
          churnRate: 0.05,
          satisfactionScore: 4.6,
          behaviorTrends: ['Premium plans', 'High data usage', 'Multiple devices'],
          color: '#95c100'
        },
        {
          id: 'loyal-users',
          name: 'Loyal Users',
          count: 3400,
          revenue: 380000,
          churnRate: 0.08,
          satisfactionScore: 4.3,
          behaviorTrends: ['Long tenure', 'Consistent usage', 'Rarely contact support'],
          color: '#1fb8cc'
        },
        {
          id: 'data-heavy',
          name: 'Data-Heavy Consumers',
          count: 1800,
          revenue: 220000,
          churnRate: 0.12,
          satisfactionScore: 4.1,
          behaviorTrends: ['Video streaming', 'Gaming', 'Work from home'],
          color: '#95c100'
        },
        {
          id: 'price-sensitive',
          name: 'Price-Sensitive',
          count: 2200,
          revenue: 145000,
          churnRate: 0.18,
          satisfactionScore: 3.8,
          behaviorTrends: ['Basic plans', 'Frequent plan changes', 'Promotional seekers'],
          color: '#95c100'
        },
        {
          id: 'high-churn-risk',
          name: 'High Churn Risk',
          count: 1400,
          revenue: 95000,
          churnRate: 0.45,
          satisfactionScore: 3.2,
          behaviorTrends: ['Multiple complaints', 'Payment issues', 'Usage decline'],
          color: '#65676e'
        }
      ],
      agent: [
        {
          id: 'high-value',
          name: 'High Value Customers',
          count: 1200,
          revenue: 250000,
          churnRate: 0.05,
          satisfactionScore: 4.6,
          behaviorTrends: ['Premium plans', 'High data usage', 'Multiple devices'],
          color: '#95c100'
        },
        {
          id: 'loyal-users',
          name: 'Loyal Users',
          count: 3400,
          revenue: 380000,
          churnRate: 0.08,
          satisfactionScore: 4.3,
          behaviorTrends: ['Long tenure', 'Consistent usage', 'Rarely contact support'],
          color: '#1fb8cc'
        },
        {
          id: 'data-heavy',
          name: 'Data-Heavy Consumers',
          count: 1800,
          revenue: 220000,
          churnRate: 0.12,
          satisfactionScore: 4.1,
          behaviorTrends: ['Video streaming', 'Gaming', 'Work from home'],
          color: '#95c100'
        },
        {
          id: 'price-sensitive',
          name: 'Price-Sensitive',
          count: 2200,
          revenue: 145000,
          churnRate: 0.18,
          satisfactionScore: 3.8,
          behaviorTrends: ['Basic plans', 'Frequent plan changes', 'Promotional seekers'],
          color: '#95c100'
        },
        {
          id: 'high-churn-risk',
          name: 'High Churn Risk',
          count: 1400,
          revenue: 95000,
          churnRate: 0.45,
          satisfactionScore: 3.2,
          behaviorTrends: ['Multiple complaints', 'Payment issues', 'Usage decline'],
          color: '#65676e'
        }
      ],
      customer: [
        {
          id: 'high-value',
          name: 'High Value Customers',
          count: 1200,
          revenue: 250000,
          churnRate: 0.05,
          satisfactionScore: 4.6,
          behaviorTrends: ['Premium plans', 'High data usage', 'Multiple devices'],
          color: '#95c100'
        },
        {
          id: 'loyal-users',
          name: 'Loyal Users',
          count: 3400,
          revenue: 380000,
          churnRate: 0.08,
          satisfactionScore: 4.3,
          behaviorTrends: ['Long tenure', 'Consistent usage', 'Rarely contact support'],
          color: '#1fb8cc'
        },
        {
          id: 'data-heavy',
          name: 'Data-Heavy Consumers',
          count: 1800,
          revenue: 220000,
          churnRate: 0.12,
          satisfactionScore: 4.1,
          behaviorTrends: ['Video streaming', 'Gaming', 'Work from home'],
          color: '#95c100'
        },
        {
          id: 'price-sensitive',
          name: 'Price-Sensitive',
          count: 2200,
          revenue: 145000,
          churnRate: 0.18,
          satisfactionScore: 3.8,
          behaviorTrends: ['Basic plans', 'Frequent plan changes', 'Promotional seekers'],
          color: '#95c100'
        },
        {
          id: 'high-churn-risk',
          name: 'High Churn Risk',
          count: 1400,
          revenue: 95000,
          churnRate: 0.45,
          satisfactionScore: 3.2,
          behaviorTrends: ['Multiple complaints', 'Payment issues', 'Usage decline'],
          color: '#65676e'
        }
      ],
    };

    return baseSegments[role as keyof typeof baseSegments] || baseSegments.customer;
  };

  const getChurnRiskCustomersForRole = (role: string): ChurnRiskCustomer[] => {
    const baseCustomers = {
      admin: [
        {
          id: 'C001',
          name: 'Sarah Johnson',
          phone: '555-0123',
          email: 'sarah.j@email.com',
          churnProbability: 0.89,
          riskFactors: ['Payment delays', '3 complaints last month', 'Usage declined 40%'],
          retentionOffers: ['20% discount for 6 months', 'Free data upgrade', 'Premium support'],
          lastActivity: '2 hours ago',
          ltv: 2400
        },
        {
          id: 'C002',
          name: 'Michael Chen',
          phone: '555-0456',
          email: 'mchen@email.com',
          churnProbability: 0.82,
          riskFactors: ['Competitor inquiry', 'Contract ending', 'Support escalation'],
          retentionOffers: ['Loyalty program enrollment', 'Device upgrade discount', 'Plan customization'],
          lastActivity: '4 hours ago',
          ltv: 1850
        },
        {
          id: 'C003',
          name: 'Emily Rodriguez',
          phone: '555-0789',
          email: 'e.rodriguez@email.com',
          churnProbability: 0.76,
          riskFactors: ['Network issues reported', 'Multiple plan downgrades', 'Low satisfaction'],
          retentionOffers: ['Priority network support', 'Service credit', 'Plan optimization'],
          lastActivity: '1 hour ago',
          ltv: 1200
        }
      ],
      agent: [
        {
          id: 'C001',
          name: 'Sarah Johnson',
          phone: '555-0123',
          email: 'sarah.j@email.com',
          churnProbability: 0.89,
          riskFactors: ['Payment delays', '3 complaints last month', 'Usage declined 40%'],
          retentionOffers: ['20% discount for 6 months', 'Free data upgrade', 'Premium support'],
          lastActivity: '2 hours ago',
          ltv: 2400
        },
        {
          id: 'C002',
          name: 'Michael Chen',
          phone: '555-0456',
          email: 'mchen@email.com',
          churnProbability: 0.82,
          riskFactors: ['Competitor inquiry', 'Contract ending', 'Support escalation'],
          retentionOffers: ['Loyalty program enrollment', 'Device upgrade discount', 'Plan customization'],
          lastActivity: '4 hours ago',
          ltv: 1850
        },
        {
          id: 'C003',
          name: 'Emily Rodriguez',
          phone: '555-0789',
          email: 'e.rodriguez@email.com',
          churnProbability: 0.76,
          riskFactors: ['Network issues reported', 'Multiple plan downgrades', 'Low satisfaction'],
          retentionOffers: ['Priority network support', 'Service credit', 'Plan optimization'],
          lastActivity: '1 hour ago',
          ltv: 1200
        }
      ],
      customer: [
        {
          id: 'C001',
          name: 'Sarah Johnson',
          phone: '555-0123',
          email: 'sarah.j@email.com',
          churnProbability: 0.89,
          riskFactors: ['Payment delays', '3 complaints last month', 'Usage declined 40%'],
          retentionOffers: ['20% discount for 6 months', 'Free data upgrade', 'Premium support'],
          lastActivity: '2 hours ago',
          ltv: 2400
        },
        {
          id: 'C002',
          name: 'Michael Chen',
          phone: '555-0456',
          email: 'mchen@email.com',
          churnProbability: 0.82,
          riskFactors: ['Competitor inquiry', 'Contract ending', 'Support escalation'],
          retentionOffers: ['Loyalty program enrollment', 'Device upgrade discount', 'Plan customization'],
          lastActivity: '4 hours ago',
          ltv: 1850
        },
        {
          id: 'C003',
          name: 'Emily Rodriguez',
          phone: '555-0789',
          email: 'e.rodriguez@email.com',
          churnProbability: 0.76,
          riskFactors: ['Network issues reported', 'Multiple plan downgrades', 'Low satisfaction'],
          retentionOffers: ['Priority network support', 'Service credit', 'Plan optimization'],
          lastActivity: '1 hour ago',
          ltv: 1200
        }
      ],
    };

    return baseCustomers[role as keyof typeof baseCustomers] || baseCustomers.customer;
  };

  const getPersonalizationOpsForRole = (role: string): PersonalizationOpportunity[] => {
    const basePersonalizations = {
      admin: [
        {
          customerId: 'P001',
          customerName: 'David Park',
          opportunity: 'Video Streaming Package',
          recommendation: 'Customer streams 4+ hours daily - offer unlimited video plan',
          confidence: 0.94,
          potentialRevenue: 25,
          triggerEvent: 'Data usage spike detected',
          timeWindow: 'Next 48 hours'
        },
        {
          customerId: 'P002',
          customerName: 'Lisa Wang',
          opportunity: 'International Roaming Add-on',
          recommendation: 'Frequent international calls detected - offer roaming package',
          confidence: 0.87,
          potentialRevenue: 45,
          triggerEvent: 'High international usage',
          timeWindow: 'Next 24 hours'
        },
        {
          customerId: 'P003',
          customerName: 'Robert Kim',
          opportunity: 'Family Plan Upgrade',
          recommendation: 'Multiple devices detected - suggest family plan savings',
          confidence: 0.91,
          potentialRevenue: 35,
          triggerEvent: 'New device activation',
          timeWindow: 'Next 72 hours'
        }
      ],
      agent: [
        {
          customerId: 'P001',
          customerName: 'David Park',
          opportunity: 'Video Streaming Package',
          recommendation: 'Customer streams 4+ hours daily - offer unlimited video plan',
          confidence: 0.94,
          potentialRevenue: 25,
          triggerEvent: 'Data usage spike detected',
          timeWindow: 'Next 48 hours'
        },
        {
          customerId: 'P002',
          customerName: 'Lisa Wang',
          opportunity: 'International Roaming Add-on',
          recommendation: 'Frequent international calls detected - offer roaming package',
          confidence: 0.87,
          potentialRevenue: 45,
          triggerEvent: 'High international usage',
          timeWindow: 'Next 24 hours'
        },
        {
          customerId: 'P003',
          customerName: 'Robert Kim',
          opportunity: 'Family Plan Upgrade',
          recommendation: 'Multiple devices detected - suggest family plan savings',
          confidence: 0.91,
          potentialRevenue: 35,
          triggerEvent: 'New device activation',
          timeWindow: 'Next 72 hours'
        }
      ],
      customer: [
        {
          customerId: 'P001',
          customerName: 'David Park',
          opportunity: 'Video Streaming Package',
          recommendation: 'Customer streams 4+ hours daily - offer unlimited video plan',
          confidence: 0.94,
          potentialRevenue: 25,
          triggerEvent: 'Data usage spike detected',
          timeWindow: 'Next 48 hours'
        },
        {
          customerId: 'P002',
          customerName: 'Lisa Wang',
          opportunity: 'International Roaming Add-on',
          recommendation: 'Frequent international calls detected - offer roaming package',
          confidence: 0.87,
          potentialRevenue: 45,
          triggerEvent: 'High international usage',
          timeWindow: 'Next 24 hours'
        },
        {
          customerId: 'P003',
          customerName: 'Robert Kim',
          opportunity: 'Family Plan Upgrade',
          recommendation: 'Multiple devices detected - suggest family plan savings',
          confidence: 0.91,
          potentialRevenue: 35,
          triggerEvent: 'New device activation',
          timeWindow: 'Next 72 hours'
        }
      ],
    };

    return basePersonalizations[role as keyof typeof basePersonalizations] || basePersonalizations.customer;
  };

  const getServiceOptimizationsForRole = (role: string): ServiceOptimization[] => {
    const baseOptimizations = {
      admin: [
        {
          category: 'Billing Issues',
          count: 156,
          avgResolutionTime: 18.5,
          satisfactionScore: 3.8,
          trend: 'down' as const,
          topIssues: ['Payment processing delays', 'Billing statement errors', 'Auto-pay failures'],
          recommendations: ['Automated payment retry', 'Enhanced billing validation', 'Proactive notifications']
        },
        {
          category: 'Network Issues',
          count: 89,
          avgResolutionTime: 45.2,
          satisfactionScore: 3.9,
          trend: 'stable' as const,
          topIssues: ['Poor signal strength', 'Data speed complaints', '5G coverage gaps'],
          recommendations: ['Tower optimization', 'Speed test automation', 'Coverage expansion']
        },
        {
          category: 'Account Management',
          count: 234,
          avgResolutionTime: 12.3,
          satisfactionScore: 4.2,
          trend: 'up' as const,
          topIssues: ['Plan changes', 'Feature additions', 'Account updates'],
          recommendations: ['Self-service enhancements', 'Plan recommendation engine', 'Streamlined workflows']
        }
      ],
      agent: [
        {
          category: 'Billing Issues',
          count: 156,
          avgResolutionTime: 18.5,
          satisfactionScore: 3.8,
          trend: 'down' as const,
          topIssues: ['Payment processing delays', 'Billing statement errors', 'Auto-pay failures'],
          recommendations: ['Automated payment retry', 'Enhanced billing validation', 'Proactive notifications']
        },
        {
          category: 'Network Issues',
          count: 89,
          avgResolutionTime: 45.2,
          satisfactionScore: 3.9,
          trend: 'stable' as const,
          topIssues: ['Poor signal strength', 'Data speed complaints', '5G coverage gaps'],
          recommendations: ['Tower optimization', 'Speed test automation', 'Coverage expansion']
        },
        {
          category: 'Account Management',
          count: 234,
          avgResolutionTime: 12.3,
          satisfactionScore: 4.2,
          trend: 'up' as const,
          topIssues: ['Plan changes', 'Feature additions', 'Account updates'],
          recommendations: ['Self-service enhancements', 'Plan recommendation engine', 'Streamlined workflows']
        }
      ],
      customer: [
        {
          category: 'Billing Issues',
          count: 156,
          avgResolutionTime: 18.5,
          satisfactionScore: 3.8,
          trend: 'down' as const,
          topIssues: ['Payment processing delays', 'Billing statement errors', 'Auto-pay failures'],
          recommendations: ['Automated payment retry', 'Enhanced billing validation', 'Proactive notifications']
        },
        {
          category: 'Network Issues',
          count: 89,
          avgResolutionTime: 45.2,
          satisfactionScore: 3.9,
          trend: 'stable' as const,
          topIssues: ['Poor signal strength', 'Data speed complaints', '5G coverage gaps'],
          recommendations: ['Tower optimization', 'Speed test automation', 'Coverage expansion']
        },
        {
          category: 'Account Management',
          count: 234,
          avgResolutionTime: 12.3,
          satisfactionScore: 4.2,
          trend: 'up' as const,
          topIssues: ['Plan changes', 'Feature additions', 'Account updates'],
          recommendations: ['Self-service enhancements', 'Plan recommendation engine', 'Streamlined workflows']
        }
      ],
    };

    return baseOptimizations[role as keyof typeof baseOptimizations] || baseOptimizations.customer;
  };

  const renderMetricCard = (metric: MetricCard, index: number) => (
    <Zoom in={!loading} timeout={500 + index * 100} key={metric.title}>
      <Grid item xs={12} sm={6} lg={3}>
        <Card
          sx={{
            background: `linear-gradient(135deg, ${metric.color}10 0%, ${metric.color}05 100%)`,
            border: `1px solid ${metric.color}20`,
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: `${animations.slideIn} 0.6s ease-out ${index * 0.1}s both`,
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 40px ${metric.color}30`,
              '&::before': {
                opacity: 1,
              },
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: metric.gradient,
              opacity: 0,
              transition: 'opacity 0.3s ease',
            },
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Avatar
                sx={{
                  background: metric.gradient,
                  width: 56,
                  height: 56,
                  animation: `${animations.float} 3s ease-in-out infinite`,
                }}
              >
                {metric.icon}
              </Avatar>
              <Chip
                label={metric.change}
                size="small"
                sx={{
                  background: metric.positive ? procogiaBrand.blue.main : '#65676e',
                  color: procogiaBrand.gray.lightest,
                  fontWeight: 600,
                  animation: `${animations.pulse} 2s ease-in-out infinite`,
                }}
              />
            </Box>
            
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: procogiaBrand.auth0.body,
                mb: 1,
                animation: `${animations.countUp} 0.8s ease-out ${index * 0.1}s both`,
              }}
            >
              {metric.value}
            </Typography>
            
            <Typography
              variant="body2"
              sx={{
                color: procogiaBrand.gray.light,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {metric.title}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Zoom>
  );

  const renderActivityItem = (activity: Activity, index: number) => (
    <Fade in={!loading} timeout={500 + index * 100} key={activity.id}>
      <ListItem
        sx={{
          borderRadius: 3,
          mb: 2,
          background: procogiaBrand.auth0.widgetBackground,
          border: `1px solid ${activity.color}20`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateX(8px)',
            boxShadow: `0 8px 24px ${activity.color}20`,
            background: `${activity.color}08`,
          },
        }}
      >
        <ListItemIcon>
          <Avatar
            sx={{
              background: activity.color,
              width: 40,
              height: 40,
              color: procogiaBrand.gray.lightest,
            }}
          >
            {activity.icon}
          </Avatar>
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: procogiaBrand.auth0.body }}>
              {activity.title}
            </Typography>
          }
          secondary={
            <Typography variant="body2" sx={{ color: procogiaBrand.gray.light, mt: 0.5 }}>
              {activity.description}
            </Typography>
          }
        />
        <ListItemSecondaryAction>
          <Typography variant="caption" sx={{ color: procogiaBrand.gray.light, fontWeight: 500 }}>
            {activity.timestamp}
          </Typography>
        </ListItemSecondaryAction>
      </ListItem>
    </Fade>
  );

  const getTabsForRole = () => {
    if (user.role === 'admin') {
      return [
        { label: 'System Status', icon: <SystemStatusIcon /> },
        { label: 'Customer Segmentation', icon: <GroupIcon /> },
        { label: 'Churn Prediction', icon: <TrendingDownIcon /> },
        { label: 'AI Personalization', icon: <PsychologyIcon /> },
        { label: 'Service Optimization', icon: <SupportIcon /> },
        { label: 'Network Map', icon: <MapIcon /> }
      ];
    } else if (user.role === 'agent') {
      return [
        { label: 'System Status', icon: <SystemStatusIcon /> },
        { label: 'AI Personalization', icon: <PsychologyIcon /> },
        { label: 'Service Optimization', icon: <SupportIcon /> },
        { label: 'Network Map', icon: <MapIcon /> }
      ];
    }
    return [];
  };

  const renderSystemStatusTab = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1fb8cc' }}>
          🖥️ Real-Time System Status
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Live monitoring of all critical system components
        </Typography>
      </Box>
      
      <SystemStatus refreshInterval={30000} />
    </Box>
  );

  const renderCustomerSegmentationTab = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1fb8cc' }}>
          Real-Time Customer Segmentation & Behavior Analysis
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select defaultValue="30d" label="Time Range">
              <MenuItem value="24h">Last 24h</MenuItem>
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
              <MenuItem value="90d">Last 90 days</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />}
            onClick={() => setLoading(true)}
            sx={{ background: 'linear-gradient(135deg, #1fb8cc 0%, #1fb8cc 100%)' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Real-time metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={2}>
          <Card sx={{ background: 'linear-gradient(135deg, #95c100 0%, #7ea000 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{realTimeData.totalCustomers.toLocaleString()}</Typography>
              <Typography variant="body2">Total Customers</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          {/* PROCOGIA BLUE ACTIVE SEGMENTS */}
          <Card sx={{ background: 'linear-gradient(135deg, #1fb8cc 0%, #1fb8cc 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{realTimeData.activeSegments}</Typography>
              <Typography variant="body2">Active Segments</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ background: 'linear-gradient(135deg, #65676e 0%, #65676e 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{realTimeData.highRiskCustomers}</Typography>
              <Typography variant="body2">High Risk</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ background: 'linear-gradient(135deg, #95c100 0%, #7ea000 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{realTimeData.personalizationHits}</Typography>
              <Typography variant="body2">AI Insights</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ background: 'linear-gradient(135deg, #95c100 0%, #7ea000 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{realTimeData.avgSatisfaction}</Typography>
              <Typography variant="body2">Avg Satisfaction</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          {/* PROCOGIA BLUE AVG RESOLUTION */}
          <Card sx={{ background: 'linear-gradient(135deg, #1fb8cc 0%, #1fb8cc 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{realTimeData.resolutionTime}h</Typography>
              <Typography variant="body2">Avg Resolution</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Customer Segments */}
      <Grid container spacing={3}>
        {customerSegments.map((segment) => (
          <Grid item xs={12} md={6} key={segment.id}>
            <Card sx={{ 
              borderLeft: `6px solid ${segment.color}`,
              '&:hover': { 
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
              },
              transition: 'all 0.3s ease'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: segment.color }}>
                    {segment.name}
                  </Typography>
                  <Chip 
                    label={`${segment.count.toLocaleString()} customers`}
                    sx={{ backgroundColor: segment.color, color: 'white' }}
                  />
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Revenue</Typography>
                    <Typography variant="h6" sx={{ color: '#95c100' }}>
                      ${segment.revenue.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Churn Rate</Typography>
                    <Typography variant="h6" sx={{ color: segment.churnRate > 0.2 ? '#65676e' : '#95c100' }}>
                      {(segment.churnRate * 100).toFixed(1)}%
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Satisfaction Score
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={(segment.satisfactionScore / 5) * 100}
                      sx={{ 
                        flexGrow: 1, 
                        height: 8, 
                        borderRadius: 4,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: segment.color
                        }
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {segment.satisfactionScore.toFixed(1)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Behavior Trends
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {segment.behaviorTrends.map((trend, index) => (
                      <Chip 
                        key={index}
                        label={trend}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: segment.color, color: segment.color }}
                      />
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderChurnPredictionTab = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1fb8cc' }}>
          Predictive Churn Detection & Retention Strategies
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Badge badgeContent={churnRiskCustomers.length} color="error">
            <Button 
              variant="contained" 
              startIcon={<NotificationsActiveIcon />}
              sx={{ background: 'linear-gradient(135deg, #65676e 0%, #65676e 100%)' }}
            >
              High Risk Alerts
            </Button>
          </Badge>
          <Button 
            variant="outlined" 
            startIcon={<MagicIcon />}
            sx={{ borderColor: '#95c100', color: '#95c100' }}
          >
            Auto-Generate Offers
          </Button>
        </Box>
      </Box>

      {/* High-risk customers table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Customers with 75%+ Churn Probability (Next 30 Days)
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Churn Risk</TableCell>
                  <TableCell>Risk Factors</TableCell>
                  <TableCell>LTV</TableCell>
                  <TableCell>Retention Offers</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {churnRiskCustomers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {customer.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {customer.phone} • {customer.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress
                          variant="determinate"
                          value={customer.churnProbability * 100}
                          size={40}
                          sx={{
                            color: customer.churnProbability > 0.8 ? '#65676e' : 
                                   customer.churnProbability > 0.6 ? '#95c100' : '#95c100'
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {(customer.churnProbability * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {customer.riskFactors.map((factor, index) => (
                          <Chip 
                            key={index}
                            label={factor}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#95c100' }}>
                        ${customer.ltv.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {customer.retentionOffers.map((offer, index) => (
                          <Chip 
                            key={index}
                            label={offer}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton color="primary" size="small">
                          <PhoneIcon />
                        </IconButton>
                        <IconButton color="primary" size="small">
                          <EmailIcon />
                        </IconButton>
                        <IconButton color="success" size="small">
                          <CampaignIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  const renderPersonalizationTab = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1fb8cc' }}>
          AI-Powered Personalization of Offers & Services
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Confidence Level</InputLabel>
            <Select defaultValue="high" label="Confidence Level">
              <MenuItem value="all">All Levels</MenuItem>
              <MenuItem value="high">High (90%+)</MenuItem>
              <MenuItem value="medium">Medium (70%+)</MenuItem>
              <MenuItem value="low">Low (50%+)</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            startIcon={<MagicIcon />}
            sx={{ background: 'linear-gradient(135deg, #95c100 0%, #7ea000 100%)' }}
          >
            Generate Offers
          </Button>
        </Box>
      </Box>

      {/* Personalization opportunities */}
      <Grid container spacing={3}>
        {personalizationOps.map((opportunity, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ 
              '&:hover': { 
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
              },
              transition: 'all 0.3s ease'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#95c100' }}>
                    {opportunity.customerName}
                  </Typography>
                  <Chip 
                    label={`${(opportunity.confidence * 100).toFixed(0)}% Confidence`}
                    color={opportunity.confidence > 0.9 ? 'success' : opportunity.confidence > 0.7 ? 'warning' : 'default'}
                  />
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {opportunity.opportunity}
                  </Typography>
                  <Typography variant="caption">
                    {opportunity.recommendation}
                  </Typography>
                </Alert>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Potential Revenue</Typography>
                    <Typography variant="h6" sx={{ color: '#95c100', fontWeight: 'bold' }}>
                      +${opportunity.potentialRevenue}/month
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Time Window</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {opportunity.timeWindow}
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip 
                    label={opportunity.triggerEvent}
                    size="small"
                    sx={{ backgroundColor: '#F3F4F6', color: '#374151' }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" sx={{ borderColor: '#1e212a', color: '#1e212a' }}>
                      Review
                    </Button>
                    <Button size="small" variant="contained" sx={{ backgroundColor: '#1fb8cc', color: 'white' }}>
                      Send Offer
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderServiceOptimizationTab = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1fb8cc' }}>
          Customer Service Optimization & Complaint Resolution
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Department</InputLabel>
            <Select defaultValue="all" label="Department">
              <MenuItem value="all">All Departments</MenuItem>
              <MenuItem value="billing">Billing</MenuItem>
              <MenuItem value="technical">Technical</MenuItem>
              <MenuItem value="sales">Sales</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            startIcon={<InsightsIcon />}
            sx={{ background: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)' }}
          >
            Generate Report
          </Button>
        </Box>
      </Box>

      {/* Service optimization metrics */}
      <Grid container spacing={3}>
        {serviceOptimizations.map((optimization, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card sx={{ 
              height: '100%',
              '&:hover': { 
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
              },
              transition: 'all 0.3s ease'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1fb8cc' }}>
                    {optimization.category}
                  </Typography>
                  <Chip 
                    label={optimization.trend}
                    color={optimization.trend === 'up' ? 'success' : optimization.trend === 'down' ? 'error' : 'default'}
                    size="small"
                  />
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Total Issues</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#65676e' }}>
                      {optimization.count}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Avg Resolution</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#95c100' }}>
                      {optimization.avgResolutionTime}h
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Satisfaction Score
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={(optimization.satisfactionScore / 5) * 100}
                      sx={{ 
                        flexGrow: 1, 
                        height: 8, 
                        borderRadius: 4,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: optimization.satisfactionScore > 4 ? '#95c100' : 
                                         optimization.satisfactionScore > 3 ? '#95c100' : '#65676e'
                        }
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {optimization.satisfactionScore.toFixed(1)}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Top Issues
                </Typography>
                <List dense>
                  {optimization.topIssues.map((issue, issueIndex) => (
                    <ListItem key={issueIndex} sx={{ px: 0 }}>
                      <ListItemText 
                        primary={issue}
                        primaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                  AI Recommendations
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {optimization.recommendations.map((rec, recIndex) => (
                    <Chip 
                      key={recIndex}
                      label={rec}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: '#95c100', color: '#95c100' }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderNetworkMapTab = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1fb8cc' }}>
          🎯 Enterprise Churn Intelligence Map
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Chip 
            label="DEMO MODE" 
            color="primary" 
            size="small"
            sx={{ backgroundColor: '#95c100', fontWeight: 'bold', color: 'white' }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>View Mode</InputLabel>
            <Select defaultValue="churn" label="View Mode">
              <MenuItem value="churn">Churn Hotspots</MenuItem>
              <MenuItem value="network">Network Towers</MenuItem>
              <MenuItem value="combined">Combined View</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />}
            onClick={() => setLoading(true)}
            sx={{ background: 'linear-gradient(135deg, #1fb8cc 0%, #1fb8cc 100%)' }}
          >
            Refresh Real-time
          </Button>
        </Box>
      </Box>
      
      {/* Enterprise Alert Bar */}
      <Alert 
        severity="warning" 
        sx={{ 
          mb: 3, 
          background: 'linear-gradient(135deg, #65676e20 0%, #1fb8cc20 100%)',
          border: '1px solid #65676e',
          '& .MuiAlert-message': { color: '#1F2937' }
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          <strong>ENTERPRISE ALERT:</strong> 3 critical churn zones detected | 
          $374K revenue at risk | 28 customers/day loss rate
        </Typography>
      </Alert>

      {/* Real-time Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={3}>
          <Card sx={{ p: 2, background: 'linear-gradient(135deg, #65676e 0%, #65676e 100%)', color: 'white' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>3,495</Typography>
            <Typography variant="caption">Customers at Risk</Typography>
          </Card>
        </Grid>
        <Grid item xs={3}>
          {/* FORCE PROCOGIA BLUE - CACHE BUST v3 */}
          <Card sx={{ p: 2, background: 'linear-gradient(135deg, #1fb8cc 0%, #7ea000 100%) !important', color: 'white !important' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>$374K</Typography>
            <Typography variant="caption">Revenue at Risk</Typography>
          </Card>
        </Grid>
        <Grid item xs={3}>
          {/* FORCE PROCOGIA GREEN - CACHE BUST v3 */}
          <Card sx={{ p: 2, background: 'linear-gradient(135deg, #95c100 0%, #7ea000 100%) !important', color: 'white !important' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>28</Typography>
            <Typography variant="caption">Daily Loss Rate</Typography>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card sx={{ p: 2, background: 'linear-gradient(135deg, #95c100 0%, #7ea000 100%)', color: 'white' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>6</Typography>
            <Typography variant="caption">Active Hotspots</Typography>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ 
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)'
      }}>
        <NetworkMap 
          height="700px"
          showCoverage={false}
          enableClustering={true}
        />
      </Card>

      {/* Demo Scenarios Quick Access */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, color: '#1fb8cc', fontWeight: 'bold' }}>
          🎭 Enterprise Demo Scenarios
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Card sx={{ p: 2, background: 'linear-gradient(135deg, #1fb8cc 0%, #1fb8cc 100%)', color: 'white' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                California Crisis
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Urban 5G Switchers - Los Angeles/SF Metro
              </Typography>
              <Chip label="73.4% Churn Risk" size="small" sx={{ backgroundColor: '#65676e', color: 'white' }} />
              <Chip label="$91K at Risk" size="small" sx={{ ml: 1, backgroundColor: '#1fb8cc', color: 'white' }} />
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card sx={{ p: 2, background: 'linear-gradient(135deg, #7ea000 0%, #95c100 100%)', color: 'white' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                Texas Family Exodus
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Price-Sensitive Multi-Line Plans
              </Typography>
              <Chip label="65.2% Churn Risk" size="small" sx={{ backgroundColor: '#65676e', color: 'white' }} />
              <Chip label="$67K at Risk" size="small" sx={{ ml: 1, backgroundColor: '#1fb8cc', color: 'white' }} />
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  // Enhanced Customer Dashboard
  if (user.role === 'customer') {
    // Generate customer data
    const generateCustomerData = (user: DashboardProps['user']) => {
      const customerProfiles = {
        'mehar.singh.80@gmail.com': {
          accountNumber: 'ACC-78451623',
          phone: '+1 (555) 123-4567',
          address: '1425 Oak Street, Suite 200, Vancouver, BC V6H 3W1',
          plan: 'Premium Unlimited',
          dataUsed: 75.6,
          dataLimit: 100,
          networkStatus: 'excellent',
          nextBillDate: '2025-08-15',
          nextBillAmount: 89.99,
          accountStatus: 'active'
        }
      };
      
      return customerProfiles[user.email as keyof typeof customerProfiles] || {
        accountNumber: 'ACC-' + Math.random().toString().substr(2, 8),
        phone: '+1 (555) 123-4567',
        address: '123 Main Street, City, Province K1A 0A6',
        plan: 'Standard Plan',
        dataUsed: Math.random() * 80 + 20,
        dataLimit: 100,
        networkStatus: 'good',
        nextBillDate: '2025-08-15',
        nextBillAmount: 59.99,
        accountStatus: 'active'
      };
    };

    const customerData = generateCustomerData(user);
    const firstName = user.name ? user.name.split(' ')[0] : 'Customer';

    return (
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        {/* Welcome Header */}
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#1fb8cc' }}>
          👋 Welcome back, {firstName}!
        </Typography>

        <Grid container spacing={3}>
          {/* Customer Profile Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, width: 56, height: 56, bgcolor: '#1fb8cc' }}>
                    {user.name ? user.name.charAt(0) : 'C'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {user.name || 'Customer'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Account: {customerData.accountNumber}
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    📧 {user.email}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    📞 {customerData.phone}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    🏠 {customerData.address}
                  </Typography>
                </Box>
                
                <Chip 
                  label={`Account Status: ${customerData.accountStatus.toUpperCase()}`} 
                  color="success" 
                  size="small" 
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Current Plan & Usage */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  📱 Current Plan & Usage
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {customerData.plan}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {customerData.dataUsed.toFixed(1)} GB of {customerData.dataLimit} GB used
                  </Typography>
                  
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={(customerData.dataUsed / customerData.dataLimit) * 100}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: '#f0f0f0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: customerData.dataUsed > 80 ? '#f39c12' : '#1fb8cc'
                        }
                      }} 
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    backgroundColor: customerData.networkStatus === 'excellent' ? '#95c100' : '#1fb8cc',
                    mr: 1 
                  }} />
                  <Typography variant="body2">
                    Network Status: {customerData.networkStatus.charAt(0).toUpperCase() + customerData.networkStatus.slice(1)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Billing Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  💳 Billing Information
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Next Bill Due: {customerData.nextBillDate}
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#1fb8cc', fontWeight: 'bold' }}>
                    ${customerData.nextBillAmount}
                  </Typography>
                </Box>
                
                <Button 
                  variant="contained" 
                  sx={{ 
                    backgroundColor: '#1fb8cc',
                    '&:hover': { backgroundColor: '#169ba8' }
                  }}
                >
                  💳 Pay Bill
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  📊 Recent Activity
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Data usage tracked"
                      secondary="2 hours ago"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Payment processed"
                      secondary="July 15, 2025"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Plan upgraded"
                      secondary="July 10, 2025"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  const tabs = getTabsForRole();

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: procogiaBrand.gradients.primary,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated background elements */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, rgba(16, 185, 129, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.3) 0%, transparent 50%)
        `,
        animation: `${keyframes`
          0%, 100% { transform: translateX(0) translateY(0) rotate(0deg); }
          33% { transform: translateX(30px) translateY(-30px) rotate(120deg); }
          66% { transform: translateX(-20px) translateY(20px) rotate(240deg); }
        `} 20s ease-in-out infinite`,
        zIndex: 0
      }} />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ 
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          margin: 2,
          minHeight: 'calc(100vh - 32px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
        }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  minHeight: 64,
                  '&.Mui-selected': {
                    color: '#1fb8cc'
                  }
                }
              }}
            >
              {tabs.map((tab, index) => (
                <Tab 
                  key={index}
                  label={tab.label} 
                  icon={tab.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab content */}
          {activeTab === 0 && renderSystemStatusTab()}
          
          {activeTab === 1 && user.role === 'admin' && renderCustomerSegmentationTab()}
          {activeTab === 2 && user.role === 'admin' && renderChurnPredictionTab()}
          {activeTab === 3 && user.role === 'admin' && renderPersonalizationTab()}
          {activeTab === 4 && user.role === 'admin' && renderServiceOptimizationTab()}
          {activeTab === 5 && user.role === 'admin' && renderNetworkMapTab()}
          
          {activeTab === 1 && user.role === 'agent' && renderPersonalizationTab()}
          {activeTab === 2 && user.role === 'agent' && renderServiceOptimizationTab()}
          {activeTab === 3 && user.role === 'agent' && renderNetworkMapTab()}
        </Box>
      </Box>
    </Box>
  );
};

export default ModernDashboard; 